import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertCircle, Plus, X } from 'lucide-react'

const ASIN_REGEX = /^[A-Z0-9]{10}$/
const SEPARATOR_REGEX = /[\s,;|]+/
const MAX_ASINS = 20

interface MultiAsinInputProps {
  asins: string[]
  setAsins: (asins: string[]) => void
}

interface ParseResult {
  added: string[]
  invalid: string[]
  duplicates: string[]
  overflow: number
}

function parseAsinInput(raw: string, existing: string[]): ParseResult {
  const candidates = raw
    .split(SEPARATOR_REGEX)
    .map((s) => s.toUpperCase().trim())
    .filter((s) => s.length > 0)

  const added: string[] = []
  const invalid: string[] = []
  const duplicates: string[] = []
  const seen = new Set(existing)
  let overflow = 0

  for (const candidate of candidates) {
    if (!ASIN_REGEX.test(candidate)) {
      invalid.push(candidate)
      continue
    }
    if (seen.has(candidate)) {
      duplicates.push(candidate)
      continue
    }
    if (existing.length + added.length >= MAX_ASINS) {
      overflow += 1
      continue
    }
    added.push(candidate)
    seen.add(candidate)
  }

  return { added, invalid, duplicates, overflow }
}

export function MultiAsinInput({ asins, setAsins }: MultiAsinInputProps) {
  const { t } = useTranslation()
  const [input, setInput] = useState('')
  const [lastResult, setLastResult] = useState<ParseResult | null>(null)

  const remaining = MAX_ASINS - asins.length

  const commit = () => {
    if (!input.trim()) return
    const result = parseAsinInput(input, asins)
    if (result.added.length > 0) {
      setAsins([...asins, ...result.added])
    }
    setLastResult(result)
    if (result.invalid.length === 0 && result.duplicates.length === 0 && result.overflow === 0) {
      setInput('')
    } else {
      setInput(
        [...result.invalid, ...result.duplicates].join('\n'),
      )
    }
  }

  const removeAsin = (index: number) => {
    setAsins(asins.filter((_, i) => i !== index))
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      commit()
    }
  }

  return (
    <div>
      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1.5">
        {t('amazonListing.batchAsinInput')}
      </label>
      <div className="flex flex-col gap-2">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value.toUpperCase())}
          onKeyDown={handleKeyDown}
          placeholder={t('amazonListing.bulkAsinPlaceholder')}
          rows={3}
          className="w-full px-4 py-2.5 bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg font-mono text-base tracking-wider focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 resize-y"
        />
        <div className="flex items-center justify-between">
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            {t('amazonListing.bulkAsinHint')}
          </p>
          <button
            type="button"
            onClick={commit}
            disabled={!input.trim() || remaining <= 0}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-500 hover:bg-orange-600 disabled:bg-neutral-300 dark:disabled:bg-neutral-600 text-white rounded-lg transition-colors text-sm"
          >
            <Plus className="h-4 w-4" />
            {t('amazonListing.addAsins')}
          </button>
        </div>
      </div>

      {lastResult && lastResult.added.length > 0 && (
        <p className="mt-2 text-xs text-emerald-600 dark:text-emerald-400">
          {t('amazonListing.bulkAdded', { count: lastResult.added.length })}
        </p>
      )}
      {lastResult && lastResult.invalid.length > 0 && (
        <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
          <AlertCircle className="h-3 w-3" />
          {t('amazonListing.bulkInvalid', {
            count: lastResult.invalid.length,
            list: lastResult.invalid.slice(0, 3).join(', '),
          })}
        </p>
      )}
      {lastResult && lastResult.duplicates.length > 0 && (
        <p className="mt-1.5 text-xs text-amber-500 flex items-center gap-1">
          <AlertCircle className="h-3 w-3" />
          {t('amazonListing.bulkDuplicates', { count: lastResult.duplicates.length })}
        </p>
      )}
      {lastResult && lastResult.overflow > 0 && (
        <p className="mt-1.5 text-xs text-amber-500 flex items-center gap-1">
          <AlertCircle className="h-3 w-3" />
          {t('amazonListing.bulkOverflow', { count: lastResult.overflow, max: MAX_ASINS })}
        </p>
      )}

      {asins.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {asins.map((asin, i) => (
            <span
              key={asin}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg text-sm font-mono"
            >
              {asin}
              <button type="button" onClick={() => removeAsin(i)} className="text-neutral-400 hover:text-red-500">
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}
      <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">
        {t('amazonListing.batchCount', { count: asins.length, max: MAX_ASINS })}
      </p>
    </div>
  )
}
